import { useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Code,
  Clock,
  Hash,
  Server,
  FileText,
  Database,
  ExternalLink,
  Copy,
  Check,
} from 'lucide-react';
import { cn, Badge } from '@centraoneplus/ui';
import type { StructuredExecutionError } from '@/services/workflow.service';
import { ExecutionProgressBar } from './ExecutionProgressBar';
import { ExecutionActionResultsList } from './ExecutionActionResult';

// Default action type labels
const DEFAULT_ACTION_LABELS: Record<string, string> = {
  set_status: 'Set Status',
  set_priority: 'Set Priority',
  assign_agent: 'Assign Agent',
  assign_team: 'Assign Team',
  auto_assign: 'Auto-assign Agent',
  add_tag: 'Add Tag',
  remove_tag: 'Remove Tag',
  send_email: 'Send Email',
  send_notification: 'Send Notification',
  trigger_webhook: 'Trigger Webhook',
  trigger_n8n_workflow: 'n8n Workflow',
  create_ticket: 'Create Ticket',
  conditional: 'If/Else Condition',
  switch: 'Switch/Case',
  stop_workflow: 'Stop Workflow',
};

// Error code badge variants
function getErrorCodeVariant(errorCode?: string): 'error' | 'warning' | 'info' {
  if (!errorCode) return 'error';
  if (errorCode.startsWith('HTTP_')) return 'error';
  if (errorCode.startsWith('VALIDATION_')) return 'warning';
  if (errorCode.startsWith('TIMEOUT')) return 'warning';
  return 'error';
}

// Error code display label
function getErrorCodeLabel(errorCode?: string): string {
  if (!errorCode) return 'ERROR';
  return errorCode.replace(/_/g, ' ');
}

interface ExecutionErrorDetailsProps {
  structuredError: StructuredExecutionError;
  actionLabels?: Record<string, string>;
  className?: string;
  defaultExpanded?: boolean;
}

/**
 * Main component to display structured error information from automation execution.
 * Shows error summary, failed action details, progress, and expandable technical details.
 */
export function ExecutionErrorDetails({
  structuredError,
  actionLabels = DEFAULT_ACTION_LABELS,
  className,
  defaultExpanded = false,
}: ExecutionErrorDetailsProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    summary,
    errorCode,
    failedActionIndex,
    failedActionType,
    details,
    inputContext,
    failedAt,
    completedActionsCount,
    totalActionsCount,
    partialResults,
  } = structuredError;

  const failedActionLabel = failedActionType
    ? actionLabels[failedActionType] || failedActionType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Unknown Action';

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // Clipboard API not available
    }
  };

  return (
    <div
      className={cn(
        'bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg overflow-hidden',
        className
      )}
    >
      {/* Header - Error Summary */}
      <div className="px-4 py-3 border-b border-red-200 dark:border-red-800">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            {/* Error code badge */}
            {errorCode && (
              <Badge
                variant={getErrorCodeVariant(errorCode)}
                size="sm"
                className="mb-2"
              >
                {getErrorCodeLabel(errorCode)}
              </Badge>
            )}
            {/* Summary message */}
            <p className="text-sm font-medium text-red-800 dark:text-red-200">
              {summary}
            </p>
            {/* Failed action info */}
            {failedActionIndex !== undefined && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                Failed at action #{failedActionIndex + 1}: {failedActionLabel}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="px-4 py-3 bg-white/50 dark:bg-gray-800/20 border-b border-red-200 dark:border-red-800">
        <ExecutionProgressBar
          completedCount={completedActionsCount}
          totalCount={totalActionsCount}
          failedIndex={failedActionIndex}
        />
      </div>

      {/* Timestamp */}
      <div className="px-4 py-2 flex items-center gap-2 text-xs text-red-600 dark:text-red-400 border-b border-red-200 dark:border-red-800">
        <Clock className="w-3 h-3" />
        <span>Failed at: {new Date(failedAt).toLocaleString()}</span>
      </div>

      {/* Expandable details section */}
      <div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-2 flex items-center justify-between text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-100/50 dark:hover:bg-red-900/20 transition-colors"
        >
          <span>View Details</span>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>

        {isExpanded && (
          <div className="px-4 pb-4 space-y-4">
            {/* Full error message */}
            {details?.message && (
              <ErrorDetailSection
                icon={FileText}
                title="Error Message"
                onCopy={() => handleCopy(details.message, 'message')}
                copied={copiedField === 'message'}
              >
                <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words bg-gray-100 dark:bg-gray-800 rounded p-2 max-h-40 overflow-auto">
                  {details.message}
                </pre>
              </ErrorDetailSection>
            )}

            {/* HTTP status */}
            {details?.httpStatus && (
              <ErrorDetailSection icon={Server} title="HTTP Status">
                <div className="flex items-center gap-2">
                  <Badge
                    variant={details.httpStatus >= 500 ? 'error' : details.httpStatus >= 400 ? 'warning' : 'info'}
                    size="sm"
                  >
                    {details.httpStatus}
                  </Badge>
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    {getHttpStatusText(details.httpStatus)}
                  </span>
                </div>
              </ErrorDetailSection>
            )}

            {/* Request info */}
            {details?.requestInfo && (
              <ErrorDetailSection
                icon={ExternalLink}
                title="Request Info"
                onCopy={() => handleCopy(JSON.stringify(details.requestInfo, null, 2), 'request')}
                copied={copiedField === 'request'}
              >
                <div className="space-y-1 text-xs">
                  {details.requestInfo.method && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 dark:text-gray-400">Method:</span>
                      <Badge variant="info" size="sm">
                        {details.requestInfo.method}
                      </Badge>
                    </div>
                  )}
                  {details.requestInfo.url && (
                    <div className="flex items-start gap-2">
                      <span className="text-gray-500 dark:text-gray-400 shrink-0">URL:</span>
                      <code className="text-gray-700 dark:text-gray-300 break-all">
                        {details.requestInfo.url}
                      </code>
                    </div>
                  )}
                  {details.requestInfo.bodyPreview && (
                    <div className="mt-2">
                      <span className="text-gray-500 dark:text-gray-400">Body Preview:</span>
                      <pre className="mt-1 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded p-2 max-h-24 overflow-auto">
                        {details.requestInfo.bodyPreview}
                      </pre>
                    </div>
                  )}
                </div>
              </ErrorDetailSection>
            )}

            {/* Response info */}
            {details?.responseInfo && (
              <ErrorDetailSection
                icon={Server}
                title="Response Info"
                onCopy={() => handleCopy(JSON.stringify(details.responseInfo, null, 2), 'response')}
                copied={copiedField === 'response'}
              >
                <div className="space-y-1 text-xs">
                  {details.responseInfo.status && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 dark:text-gray-400">Status:</span>
                      <Badge
                        variant={details.responseInfo.status >= 500 ? 'error' : details.responseInfo.status >= 400 ? 'warning' : 'success'}
                        size="sm"
                      >
                        {details.responseInfo.status}
                      </Badge>
                    </div>
                  )}
                  {details.responseInfo.bodyPreview && (
                    <div className="mt-2">
                      <span className="text-gray-500 dark:text-gray-400">Body Preview:</span>
                      <pre className="mt-1 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded p-2 max-h-24 overflow-auto">
                        {details.responseInfo.bodyPreview}
                      </pre>
                    </div>
                  )}
                </div>
              </ErrorDetailSection>
            )}

            {/* Input context */}
            {inputContext && (inputContext.entityId || inputContext.entitySnapshot) && (
              <ErrorDetailSection
                icon={Database}
                title="Input Context"
                onCopy={() => handleCopy(JSON.stringify(inputContext, null, 2), 'context')}
                copied={copiedField === 'context'}
              >
                <div className="space-y-2 text-xs">
                  {inputContext.entityType && inputContext.entityId && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 dark:text-gray-400">Entity:</span>
                      <Badge variant="secondary" size="sm">
                        {inputContext.entityType}
                      </Badge>
                      <code className="text-gray-700 dark:text-gray-300 font-mono">
                        {inputContext.entityId.slice(0, 8)}...
                      </code>
                    </div>
                  )}
                  {inputContext.entitySnapshot && (
                    <div className="mt-2">
                      <span className="text-gray-500 dark:text-gray-400">Entity Snapshot:</span>
                      <pre className="mt-1 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded p-2 max-h-32 overflow-auto">
                        {JSON.stringify(inputContext.entitySnapshot, null, 2)}
                      </pre>
                    </div>
                  )}
                  {inputContext.resolvedVariables && Object.keys(inputContext.resolvedVariables).length > 0 && (
                    <div className="mt-2">
                      <span className="text-gray-500 dark:text-gray-400">Resolved Variables:</span>
                      <div className="mt-1 grid grid-cols-2 gap-1">
                        {Object.entries(inputContext.resolvedVariables).map(([key, value]) => (
                          <div key={key} className="flex items-center gap-1">
                            <code className="text-purple-600 dark:text-purple-400">{key}:</code>
                            <span className="text-gray-700 dark:text-gray-300 truncate">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </ErrorDetailSection>
            )}

            {/* Partial results (actions that succeeded before failure) */}
            {partialResults && partialResults.length > 0 && (
              <ErrorDetailSection icon={Hash} title="Actions Before Failure">
                <ExecutionActionResultsList
                  results={partialResults}
                  actionLabels={actionLabels}
                />
              </ErrorDetailSection>
            )}

            {/* Stack trace (collapsed by default for brevity) */}
            {details?.stackTrace && (
              <StackTraceSection
                stackTrace={details.stackTrace}
                onCopy={() => handleCopy(details.stackTrace || '', 'stack')}
                copied={copiedField === 'stack'}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Helper component for detail sections
interface ErrorDetailSectionProps {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
  onCopy?: () => void;
  copied?: boolean;
}

function ErrorDetailSection({
  icon: Icon,
  title,
  children,
  onCopy,
  copied,
}: ErrorDetailSectionProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
          <Icon className="w-3.5 h-3.5" />
          <span>{title}</span>
        </div>
        {onCopy && (
          <button
            onClick={onCopy}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            {copied ? (
              <>
                <Check className="w-3 h-3 text-green-500" />
                <span>Copied</span>
              </>
            ) : (
              <>
                <Copy className="w-3 h-3" />
                <span>Copy</span>
              </>
            )}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

// Stack trace section with its own collapse state
interface StackTraceSectionProps {
  stackTrace: string;
  onCopy?: () => void;
  copied?: boolean;
}

function StackTraceSection({ stackTrace, onCopy, copied }: StackTraceSectionProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="space-y-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
      >
        <Code className="w-3.5 h-3.5" />
        <span>Stack Trace</span>
        {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {isOpen && (
        <div className="relative">
          {onCopy && (
            <button
              onClick={onCopy}
              className="absolute top-2 right-2 flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors bg-gray-200 dark:bg-gray-700 rounded px-1.5 py-0.5"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3 text-green-500" />
                  <span>Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  <span>Copy</span>
                </>
              )}
            </button>
          )}
          <pre className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded p-3 max-h-48 overflow-auto font-mono">
            {stackTrace}
          </pre>
        </div>
      )}
    </div>
  );
}

// Helper function for HTTP status text
function getHttpStatusText(status: number): string {
  const statusTexts: Record<number, string> = {
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    408: 'Request Timeout',
    409: 'Conflict',
    422: 'Unprocessable Entity',
    429: 'Too Many Requests',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    504: 'Gateway Timeout',
  };
  return statusTexts[status] || 'Unknown Error';
}

export default ExecutionErrorDetails;
